/**
 * The Investor Portals connector row on the Connectors settings page (#11635).
 *
 * One row with a rollup ("3 need attention — 2 synced, 1 enrolling —
 * Last sync: 2h ago"), expandable to one line per service (Juniper Square,
 * Allvue, an unrecognized platform): the service's favicon, its name, and
 * its state with a timestamp — `Synced — Last sync: 2h ago`, `Enrolling
 * — since 3h ago`, `Needs attention — since 1h ago`. Each line is a link to
 * the Documents view narrowed to that service (Round 5, R6), where its
 * mail and its documents are.
 *
 * That is the whole row. Services arrive through the inbox and are
 * registered on the backend; the row carries no button, no login
 * ownership, no manager roster and no failure reason (Hunter, Sep 22:
 * enrollment stays on the backend for now, and the page details each
 * service, when it last had activity, and its state, "simple as that").
 *
 * Two things worth knowing before changing this:
 *
 * - **Logins are write-only.** The list API returns metadata only, this
 *   component never receives a stored username or password.
 * - **The favicon is the browser's.** The API emits the service's host;
 *   `ServiceFavicon` fetches the image and falls back to the Investor
 *   Portals mark when there is none.
 *
 * Spec: `.claude/features/11635-portal-documents-ui.md`,
 * `.claude/features/11635-portal-audit-view.md`.
 */
import { GlobeIcon } from "@phosphor-icons/react";
import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronUp, Loader2 } from "lucide-react";
import fpFetch from "@/services/fpFetch";
import { BASE_API_URL_V2 } from "@/constants";
import { ServiceFavicon } from "../../../common/ServiceFavicon";
import {
  compareCalendarDays,
  formatTemporal,
  type Temporal,
} from "@/utils/temporal";
import { useIsPhoneViewport } from "@/hooks/useViewportTier";
import { formatRelativeTemporal } from "./relativeTime";
import type { Permission } from "@/hooks/useCurrentUser";

// ─── Types ─────────────────────────────────────────────────────────

export type PortalServiceState = "enrolling" | "needs_attention" | "connected";

/** Where a service is on its way from invitation to a complete history
 *  (#13095 step 3b), derived on the backend from its records. */
export type PortalServiceStage =
  | "invited"
  | "registered"
  | "syncing"
  | "synced"
  | "waiting_on_you"
  | "failing";

/** The person's step a Waiting on you service is stopped on. */
export type PortalPersonStep =
  | "signature"
  | "captcha"
  | "device"
  | "invitation"
  | "agreement"
  | "locked"
  | "login";

/** How much of a service's history we hold (#13095 step 3b). */
export interface PortalCoverage {
  /** The oldest and newest day walked: calendar days, never instants. */
  earliest: Temporal | null;
  latest: Temporal | null;
  /** The total the portal shows; null when it shows none. */
  total: number | null;
  /** The portal stops counting at its row cap ("1000+"). */
  total_is_floor: boolean;
  /** The documents we hold from the service. */
  held: number;
  /** The walk reached the portal's oldest document. */
  reached_start: boolean;
  /** The start was claimed on a listing with no dates and no total. */
  unverifiable: boolean;
  /** The floor the walk ran from (#13095 step 3c): a calendar day; null for
   *  a walk of all history. */
  history_from: Temporal | null;
}

export interface PortalService {
  connection_id: number;
  portal_url: string | null;
  entity_id: string | null;
  entity_kind: string | null;
  entity_name: string | null;
  /** When the stored login was last set; null until one is stored. Never
   * the login itself. */
  credential_set_at: Temporal | null;
  credential_set_by: string | null;
  service: string;
  platform: string | null;
  managers: string[];
  login_owner: "finpilot" | "client";
  state: PortalServiceState;
  /** Null for a service that needs attention: the state says it, and no
   *  reason reaches a person (#11635 Round 19, R68). Nothing renders it. */
  state_detail: string | null;
  /** Why the service needs attention (runner output, an exception, a
   *  page's ask), for an engineer reading the API; never rendered. */
  diagnostic: string | null;
  /** The instant the service entered its state; null when unknown (and for
   * a connected service, whose activity is its last sync). */
  state_since: Temporal | null;
  /** The host whose favicon marks the service, or null when unknown. */
  icon_host: string | null;
  registering: boolean;
  mapped: boolean;
  invitation_received_at: Temporal | null;
  last_sync_at: Temporal | null;
  /** Documents that arrived through the service in the last
   *  `PORTAL_ACTIVITY_WINDOW_DAYS` days (#11635 Round 4, spec R21). */
  recent_document_count: number;
  /** The newest document that arrived through the service, ever; null when
   *  it has delivered none. */
  last_document_at: Temporal | null;
  /** The service's stage (#13095 step 3b). The stage fields are an
   *  engineer's read, like `diagnostic`: no surface renders their text, so
   *  no person-step ask reaches a client (Round 19, R68). */
  stage: PortalServiceStage;
  /** Failing: a plain phrase for what went wrong. Waiting on you: the
   *  action to take. */
  stage_detail: string | null;
  /** The full text behind the detail: the latest run's error, the
   *  portal's ask. */
  stage_reason: string | null;
  stage_step: PortalPersonStep | null;
  /** Null before the first sync recorded one. */
  coverage: PortalCoverage | null;
  /** The day the service's history is kept from (#13095 step 3c): its own,
   *  or the organization's default; null keeps all history. */
  history_from: Temporal | null;
  /** Where that day comes from: the day the service was registered (the
   *  organization's default), a date, or all history. */
  history_basis: "registration" | "date" | "all";
  /** A sync is running now. */
  syncing: boolean;
  /** When the sync running now started; null when none is running (#11635,
   *  the portal-status-accuracy backend). */
  syncing_since: Temporal | null;
  /** The manager records the service's documents are linked to, all time,
   *  most documents first (#11635 slice 1, Round 9, R28). */
  delivered_managers: DeliveredManager[];
  /** Listed documents the syncs tried three times and could not keep, left
   *  alone since (#13095 R-R1a). */
  given_up: number;
  /** Up to three kinds of failure among them, newest first. */
  given_up_reasons: GivenUpReason[];
}

/** One kind of failure a service's syncs gave up on (#13095 R-R1a). */
export interface GivenUpReason {
  /** The kind's key, for code. */
  reason_kind: string;
  /** The kind in plain words, which the page shows. */
  reason_text: string;
  count: number;
}

/** A manager a service delivers documents for (R28). */
export interface DeliveredManager {
  public_id: string;
  name: string;
  document_count: number;
  /** The newest such document's upload; null when unknown. */
  newest_document_at: Temporal | null;
}

const dayOf = (value: Temporal): string | null =>
  value.granularity === "date" ? value.day : null;

/** How far back the service's history is kept: "Since registration (Oct 7,
 *  2026)", "History from Oct 2025", or "All history". A floor raised after
 *  the walk reached an earlier one drops nothing held, so the card then
 *  names the walk's floor: what is actually kept. */
export const historyText = (service: PortalService): string => {
  const walked = service.coverage?.history_from ?? null;
  const floor = service.history_from;
  if (floor && walked) {
    const floorDay = dayOf(floor);
    const walkedDay = dayOf(walked);
    if (floorDay && walkedDay && compareCalendarDays(walkedDay, floorDay) < 0) {
      return `History from ${formatTemporal(walked, "monthYear")}`;
    }
  }
  if (!floor || service.history_basis === "all") return "All history";
  if (service.history_basis === "registration") {
    return `Since registration (${formatTemporal(floor, "medium")})`;
  }
  return `History from ${formatTemporal(floor, "monthYear")}`;
};

/** The window the Documents overview counts a service's documents over.
 *  The backend counts with the same number (`PORTAL_ACTIVITY_WINDOW_DAYS`
 *  in the portal credentials route); change both together. */
export const PORTAL_ACTIVITY_WINDOW_DAYS = 7;

export interface PortalServicesResponse {
  connections: PortalService[];
  enrollment: { email: string | null; phone: string };
}

// ─── API ───────────────────────────────────────────────────────────

export const PORTAL_KEYS = {
  all: ["portal-credentials"] as const,
};

const LIST_URL = `${BASE_API_URL_V2}/api/v2/connections/portals/credentials`;

const fetchPortalServices = async (): Promise<PortalServicesResponse> => {
  const response = await fpFetch(LIST_URL);
  if (!response.ok) {
    throw new Error(`Failed to load investor portals: ${response.status}`);
  }
  return response.json();
};

/** The organization's portal services and enrollment address. Exported so
 * the page can decide whether the row exists at all. */
export const usePortalServices = () =>
  useQuery<PortalServicesResponse>({
    queryKey: PORTAL_KEYS.all,
    queryFn: fetchPortalServices,
    staleTime: 30 * 1000,
    // A registration run takes minutes; while one is in progress the row
    // re-reads so Enrolling advances to Connected or Needs attention
    // without a reload.
    refetchInterval: (query) =>
      query.state.data?.connections.some((service) => service.registering)
        ? 15_000
        : false,
  });

/** A service's own page in Documents (Round 5, R6; a service is a folder
 * since #11635 slice 1, Round 7, R21): the page the sidebar's service row
 * and the overview's tile open. */
export const portalDocumentsPath = (service: string): string =>
  `/documents?view=portals&svc=${encodeURIComponent(service)}`;

// ─── Presentation helpers ──────────────────────────────────────────

const relativeOf = formatRelativeTemporal;

const instantMs = (value: Temporal | null): number | null => {
  if (value === null) return null;
  switch (value.granularity) {
    case "instant":
      return new Date(value.at).getTime();
    case "date":
      return null;
    default: {
      const unhandled: never = value;
      throw new Error(
        `unhandled temporal granularity: ${JSON.stringify(unhandled)}`
      );
    }
  }
};

interface StateStyle {
  dotClass: string;
  textClass: string;
  label: string;
}

const STATE_STYLES: Record<PortalServiceState, StateStyle> = {
  connected: {
    dotClass: "bg-green-500",
    textClass: "text-green-600",
    label: "Synced",
  },
  enrolling: {
    dotClass: "bg-amber-500",
    textClass: "text-amber-600",
    label: "Enrolling",
  },
  needs_attention: {
    dotClass: "bg-red-500",
    textClass: "text-red-600",
    label: "Needs attention",
  },
};

const styleOf = (state: PortalServiceState): StateStyle => STATE_STYLES[state];

/** The clause after the state label (R2): the last sync for a connected
 * service, the instant the state was entered otherwise. Never a reason. */
const stateDetail = (service: PortalService): string | null => {
  switch (service.state) {
    case "connected":
      return service.last_sync_at
        ? `Last sync: ${relativeOf(service.last_sync_at)}`
        : null;
    case "enrolling":
    case "needs_attention":
      return service.state_since
        ? `since ${relativeOf(service.state_since)}`
        : null;
    default: {
      const unhandled: never = service.state;
      throw new Error(`unhandled portal state: ${JSON.stringify(unhandled)}`);
    }
  }
};

const STATE_RANK: Record<PortalServiceState, number> = {
  needs_attention: 0,
  enrolling: 1,
  connected: 2,
};

/** The instant a service's line is dated by: when it entered Enrolling or
 * Needs attention, or a connected service's last sync. */
const activityMs = (service: PortalService): number | null =>
  instantMs(
    service.state === "connected" ? service.last_sync_at : service.state_since
  );

/** The expanded row's order (R14): Needs attention first, then Enrolling,
 * then Connected; within a state the most recent first, services with no
 * instant last, then by name. */
const sortServices = (services: PortalService[]): PortalService[] =>
  [...services].sort((a, b) => {
    const rank = STATE_RANK[a.state] - STATE_RANK[b.state];
    if (rank !== 0) return rank;
    const at = activityMs(a);
    const bt = activityMs(b);
    if (at !== bt) {
      if (at === null) return 1;
      if (bt === null) return -1;
      return bt - at;
    }
    return a.service.localeCompare(b.service);
  });

export interface PortalRollup {
  connected: number;
  enrolling: number;
  needsAttention: number;
  /** The newest completed sync across every service, as a wire instant. */
  lastSync: Temporal | null;
}

export const rollupOf = (services: PortalService[]): PortalRollup => {
  const rollup: PortalRollup = {
    connected: 0,
    enrolling: 0,
    needsAttention: 0,
    lastSync: null,
  };
  let newest = -1;
  for (const service of services) {
    if (service.state === "connected") rollup.connected += 1;
    else if (service.state === "enrolling") rollup.enrolling += 1;
    else if (service.state === "needs_attention") rollup.needsAttention += 1;
    const ms = instantMs(service.last_sync_at);
    if (ms !== null && ms > newest) {
      newest = ms;
      rollup.lastSync = service.last_sync_at;
    }
  }
  return rollup;
};

/** The rollup line, clauses with a zero count omitted (R1). */
export const rollupText = (
  rollup: PortalRollup
): { primary: string; primaryState: PortalServiceState; rest: string[] } => {
  // What needs a look leads (#13095 step 7, R-P4), then what is synced,
  // then what is still enrolling.
  const clauses: [PortalServiceState, string][] = [];
  const attention = rollup.needsAttention;
  if (attention > 0) {
    clauses.push([
      "needs_attention",
      `${attention} need${attention === 1 ? "s" : ""} attention`,
    ]);
  }
  if (rollup.connected > 0)
    clauses.push(["connected", `${rollup.connected} synced`]);
  if (rollup.enrolling > 0)
    clauses.push(["enrolling", `${rollup.enrolling} enrolling`]);
  if (clauses.length === 0) {
    return { primary: "No services yet", primaryState: "enrolling", rest: [] };
  }
  const [primaryState, primary] = clauses[0];
  return {
    primary,
    primaryState,
    rest: clauses.slice(1).map(([, text]) => text),
  };
};

// ─── Component ─────────────────────────────────────────────────────

/** Whether the viewer may read the portals mail: the organization's admin
 * (the mails carry temporary passwords and single-use links; mail view spec
 * M7). A staff member is one only inside a client, as a collaborator whose
 * grant was elevated, which the org entry says (#10718). A pre-roles entry
 * with neither flag is the admin-era viewer every other admin control treats
 * as one. The Documents view reads this too, for the service header's
 * sign-ins count (R31). */
export const useCanReadPortalMail = (): boolean =>
  useSelector((state: { userData?: { permissions?: Permission[] } }) => {
    const permissions = state.userData?.permissions ?? [];
    const perm = permissions.find((p) => p.organizationId);
    if (!perm) return false;
    if (perm.admin === true) return true;
    return perm.admin === undefined && perm.manager === undefined;
  });

export interface InvestorPortalsRowProps {
  expanded: boolean;
  onToggleExpand: () => void;
}

export const InvestorPortalsRow: React.FC<InvestorPortalsRowProps> = ({
  expanded,
  onToggleExpand,
}) => {
  const phone = useIsPhoneViewport();
  const { data, isLoading, error } = usePortalServices();

  const services = data?.connections ?? [];
  const rollup = rollupOf(services);
  const line = rollupText(rollup);
  const primaryStyle = styleOf(line.primaryState);

  // On a phone the rollup wraps under the name and the whole line is kept:
  // truncating "1 synced, 2 enrolling — Last sync" beside the chevron
  // would drop the clauses the row exists to show (#11742 round 7 shape).
  const statusCluster = (
    <div className="flex min-w-0 items-start gap-2 md:items-center md:shrink md:max-w-[30rem]">
      {isLoading ? (
        <Loader2 className="w-4 h-4 shrink-0 animate-spin text-gray-400" />
      ) : (
        <span
          className={`inline-block w-2.5 h-2.5 mt-[5px] md:mt-0 shrink-0 rounded-full ${
            services.length === 0 ? "bg-gray-300" : primaryStyle.dotClass
          }`}
        />
      )}
      <span className="min-w-0 text-sm md:truncate md:whitespace-nowrap">
        {isLoading ? (
          <span className="text-gray-500">Loading...</span>
        ) : error ? (
          <span className="text-red-600">Could not load</span>
        ) : (
          <>
            <span
              className={
                services.length === 0 ? "text-gray-500" : primaryStyle.textClass
              }
            >
              {line.primary}
            </span>
            {line.rest.length > 0 && (
              <span className="text-gray-500">{` — ${line.rest.join(", ")}`}</span>
            )}
            {rollup.lastSync && (
              <span className="text-gray-500">
                {" — Last sync: "}
                {relativeOf(rollup.lastSync)}
              </span>
            )}
          </>
        )}
      </span>
    </div>
  );

  return (
    <div>
      <div className="flex items-center px-4 py-3 gap-3">
        <div className="shrink-0 w-5 h-5 flex items-center justify-center">
          <GlobeIcon className="w-5 h-5 text-gray-500" />
        </div>
        <div className="min-w-0 flex-1 flex flex-col justify-center min-h-[2.5rem]">
          <div className="flex min-w-0 items-center gap-2">
            <span className="min-w-0 truncate text-sm font-medium text-gray-900">
              Investor Portals
            </span>
          </div>
          {phone && statusCluster}
        </div>
        {!phone && statusCluster}
        <button
          type="button"
          onClick={onToggleExpand}
          aria-label={
            expanded ? "Collapse Investor Portals" : "Expand Investor Portals"
          }
          className="shrink-0 text-gray-400 hover:text-gray-600 focus:outline-none"
        >
          {expanded ? (
            <ChevronUp className="w-5 h-5" />
          ) : (
            <ChevronDown className="w-5 h-5" />
          )}
        </button>
      </div>

      {expanded && (
        <div className="bg-gray-50 border-t border-gray-200">
          {services.length === 0 && !isLoading && (
            <div className="px-4 md:px-10 py-3 text-sm text-gray-500">
              No services yet.
            </div>
          )}

          {services.length > 0 && (
            <ul className="divide-y divide-gray-200">
              {sortServices(services).map((service) => (
                <ServiceLine key={service.connection_id} service={service} />
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

// ─── One service ───────────────────────────────────────────────────

/** One service's line: a link to its page in Documents (R6), the whole line
 * clickable; the hover is the only affordance (Hunter, Sep 29). */
const ServiceLine: React.FC<{ service: PortalService }> = ({ service }) => {
  const style = styleOf(service.state);
  const detail = stateDetail(service);
  return (
    <li>
      <Link
        to={portalDocumentsPath(service.service)}
        aria-label={service.service}
        className="group px-4 md:px-10 py-3 flex flex-col gap-1 md:flex-row md:items-center md:gap-4 hover:bg-gray-100"
      >
        <span className="flex min-w-0 flex-1 items-center gap-2">
          <ServiceFavicon
            host={service.icon_host}
            label={service.service}
            size={16}
            fallback={
              <GlobeIcon
                className="w-4 h-4 shrink-0 text-gray-400"
                role="img"
                aria-label={service.service}
              />
            }
          />
          <span className="flex min-w-0 flex-col">
            <span className="min-w-0 truncate text-sm font-medium text-gray-900">
              {service.service}
            </span>
            {/* A service on no known platform says what site it is
                (enrollment spec R23): the name alone does not. */}
            {service.platform === null && service.icon_host && (
              <span
                data-testid="service-host"
                className="truncate text-xs text-gray-500"
              >
                {service.icon_host}
              </span>
            )}
          </span>
        </span>
        <span className="flex shrink-0 items-center gap-2 md:justify-end">
          <span
            className={`inline-block w-2.5 h-2.5 shrink-0 rounded-full ${style.dotClass}`}
          />
          <span className="text-sm whitespace-nowrap">
            <span className={style.textClass}>{style.label}</span>
            {detail && <span className="text-gray-500">{` — ${detail}`}</span>}
          </span>
        </span>
      </Link>
    </li>
  );
};
